import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PlateProvider } from '@udecode/plate';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import ActionBar from 'components/text_editor_new/components/action_bar/action_bar';
import { Attachment, Emoji } from 'components/composer/shared/editor_action_buttons_new';
import AttachmentsDropZone, { useDroppable } from 'components/composer/shared/attachments_drop_zone';
import { BoldItalicUnderlineControls, ListControls, Separator } from 'components/composer/shared/editor_controls_new';
import Button, { ButtonTypes } from 'components/common/button';
import ComposerCancelModal from 'components/composer/shared/composer_cancel_modal';
import ComposerContext, { ComposerContextProviderBase } from 'components/composer/contexts/composer_context';
import ComposerEditor from 'components/composer/shared/composer_editor';
import Composition from 'models/composition';
import CompositionAttachmentsContainer from 'components/customer/composition/lib/attachments/composition_attachments_container';
import connect from 'components/lib/connect';
import CreateTaskThreadReplyComposition from 'actions/task/create_task_thread_reply_composition';
import { createUrlHighlightPlugin } from 'components/text_editor_new/plugins/inline_links/create_url_highlight_plugin';
import CustomerContext from 'components/customer/customer_context';
import DatetimePickerMenu from 'components/lib/datetime_picker_menu';
import DiscardTaskThreadReply from 'actions/task/discard_task_thread_reply';
import EditorErrors from 'components/composer/shared/editor_errors';
import Err from 'models/err';
import { findTaskThreadReplyComposition } from 'actions/composition/lib/composer_compositions';
import FontColorControls from 'components/composer/shared/font_color_controls';
import ForgottenAttachmentModal from 'components/composer/email/forgotten_attachment_modal';
import ForgottenTextModal from 'components/composer/email/forgotten_text_modal';
import From from 'components/composer/email/from';
import { getDatetimeFromTimestamp, getTimestampFromDatetime } from 'components/lib/date/datetime_helpers';
import InsertInlineImageButton from 'components/composer/shared/insert_inline_image_button_new';
import { isMacOS } from 'scripts/lib/browser_detector';
import { LinkButton } from 'components/composer/inline_links/link_button';
import { LinkEditor } from 'components/composer/inline_links/link_editor';
import moment from 'moment';
import nextDay from 'scripts/lib/next_day';
import Recipients from 'components/composer/email/recipients';
import SendTaskThreadReply from 'actions/composer/send_task_thread_reply';
import serializeEmailBody from 'components/text_editor_new/lib/serialize_email_body';
import { serializePlaintext } from 'components/text_editor_new/lib/serialize_plaintext';
import TableMenu from 'components/composer/shared/editor_table_controls_new';
import TextAlignmentMenu from 'components/composer/shared/editor_alignment_controls_new';
import { TextEditorWrapper } from 'components/customer/task_comments/task_comment_editor_new_styles';
import UpdateComposition from 'actions/composer/update_composition';
import useComposer from 'components/composer/shared/use_composer';
import useEditor from 'components/composer/shared/use_editor';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import useForgottenTextModal from 'components/composer/email/use_forgotten_text_modal';
import useInitialValue from 'components/text_editor_new/hooks/use_initial_value';
import useRichTextEditorPlugins from 'components/text_editor_new/hooks/use_rich_text_editor_plugins';
import { useSubmitOnHotkey } from 'components/composer/shared/use_submit_on_hotkey';

// The task panel's reply is a real, persisted composition rendered outside the composer tabs. The
// composer machinery (uploads, inline images, drafts, errors) keys everything on the composition
// found in ComposerContext and the id in CustomerContext, so both are provided here for this
// subtree only: the main composer keeps its own.
export function TaskThreadReplyComposer({
  composition,
  compositionErrors,
  cc,
  conversationId,
  from,
  isExperimentalEditorEnabled,
  isLoadingCompositions,
  isSending,
  onCancel,
  subject,
  taskId,
  to,
}) {
  const executeAction = useExecuteAction();
  const customerContext = useContext(CustomerContext);
  const compositionId = composition && composition.id;

  // The seed is read when the draft is created, so a re-render with equal recipients doesn't re-run the effect.
  const seedRef = useRef();
  seedRef.current = { conversationId, from, to, cc, subject };
  useEffect(() => {
    if (!composition && !isLoadingCompositions) {
      executeAction(CreateTaskThreadReplyComposition, { taskId, ...seedRef.current });
    }
  }, [composition, executeAction, isLoadingCompositions, taskId]);

  const onDiscard = useCallback(() => {
    if (isSending) return;
    if (compositionId) {
      executeAction(DiscardTaskThreadReply, { compositionId });
    }
    if (onCancel) onCancel();
  }, [compositionId, executeAction, isSending, onCancel]);

  const customerContextValue = useMemo(() => ({ ...customerContext, compositionId }), [customerContext, compositionId]);

  // Only the draft the reply opened on takes focus. The one that replaces it after a send or discard
  // remounts the editor, and focusing that would pull the agent out of whatever they had moved on to.
  const focusDraftIdRef = useRef();
  if (compositionId && !focusDraftIdRef.current) focusDraftIdRef.current = compositionId;

  if (!composition) {
    return null;
  }

  return (
    <CustomerContext.Provider value={customerContextValue}>
      <ComposerContextProviderBase
        attachSnippetAttachments={_.noop}
        clearInsertedAnswer={_.noop}
        composition={composition}
        compositionErrors={compositionErrors}
        isExperimentalEditorEnabled={isExperimentalEditorEnabled}
        isMinimized={false}
      >
        <TaskThreadReplyComposerBase
          autoFocus={composition.id === focusDraftIdRef.current}
          isSending={isSending}
          key={composition.id}
          onDiscard={onDiscard}
        />
      </ComposerContextProviderBase>
    </CustomerContext.Provider>
  );
}

TaskThreadReplyComposer.propTypes = {
  composition: PropTypes.instanceOf(Composition),
  compositionErrors: PropTypes.arrayOf(PropTypes.instanceOf(Err)),
  cc: PropTypes.arrayOf(PropTypes.string),
  conversationId: PropTypes.string,
  from: PropTypes.string,
  isExperimentalEditorEnabled: PropTypes.bool,
  isLoadingCompositions: PropTypes.bool,
  isSending: PropTypes.bool,
  onCancel: PropTypes.func,
  subject: PropTypes.string,
  taskId: PropTypes.string.isRequired,
  to: PropTypes.arrayOf(PropTypes.string),
};

function TaskThreadReplyComposerBase({ autoFocus, isSending, onDiscard }) {
  const { t } = useTranslation();
  const { composition } = useContext(ComposerContext);
  const executeAction = useExecuteAction();
  const compositionId = composition.id;

  const subjectRef = useRef(composition.content.subject || '');
  const [subject, setSubject] = useState(subjectRef.current);
  const onChangeSubject = useCallback(evt => {
    subjectRef.current = evt.target.value;
    setSubject(evt.target.value);
  }, []);
  const saveSubject = useCallback(
    () => executeAction(UpdateComposition, { compositionId, attrs: { subject: subjectRef.current } }),
    [compositionId, executeAction]
  );
  const createContent = useCallback((editor, attr, content) => {
    if (attr === 'bodyHtml') {
      return { ...content, bodyPlain: serializePlaintext(editor), subject: subjectRef.current };
    }
    return content;
  }, []);

  const composerProps = useComposer({ action: SendTaskThreadReply, options: { createContent } });
  const editorProps = useEditor('bodyHtml', composerProps, {
    dontAutofocus: !autoFocus,
    serializer: serializeEmailBody,
  });
  const extraPlugins = useMemo(() => [createUrlHighlightPlugin()], []);
  const editorRef = useRichTextEditorPlugins(composerProps, editorProps, extraPlugins, VENDOR_EDITOR_OPTIONS);
  const initialValue = useInitialValue(editorProps.initialHtml, editorRef.current);
  const [dropProps, isDragActive] = useDroppable(!isSending);

  const {
    onSubmit,
    showForgottenTextModal,
    clearShowForgottenTextModal,
    forgottenAttachmentText,
    clearForgottenAttachmentText,
  } = useForgottenTextModal(composerProps.onSubmit, editorProps.editorRef.current);

  // The draft keeps its follow-up date, so one reopened after that date has passed starts from tomorrow.
  const storedDueAt = composition.content.dueAt;
  const [initialDueAt] = useState(() => (storedDueAt && moment(storedDueAt).isBefore() ? nextDay() : storedDueAt));
  useEffect(() => {
    if (initialDueAt !== storedDueAt) {
      executeAction(UpdateComposition, { compositionId, attrs: { dueAt: initialDueAt } });
    }
  }, [compositionId, executeAction, initialDueAt, storedDueAt]);
  const [followUp, setFollowUp] = useState(() => getDatetimeFromTimestamp(initialDueAt));
  const onChangeFollowUp = useCallback(
    attrs => {
      if (!attrs.date) return;
      setFollowUp(attrs);
      executeAction(UpdateComposition, { compositionId, attrs: { dueAt: getTimestampFromDatetime(attrs) } });
    },
    [compositionId, executeAction]
  );

  // The subject is pre-filled, so only the body and attachments decide whether there is a draft to lose.
  // While a send is out the draft can't go: removing it would delete the uploads the send carries.
  const { setIsCancelling } = composerProps;
  const onCancel = useCallback(() => {
    if (isSending) return;
    const hasText = !!_.trim(serializePlaintext(editorRef.current));
    if (hasText || composition.attachments.length) {
      setIsCancelling(true);
    } else {
      onDiscard();
    }
  }, [composition, editorRef, isSending, onDiscard, setIsCancelling]);

  const keyboardHotkeys = ['Mod+Enter'];
  if (isMacOS()) keyboardHotkeys.push('Control+Enter');
  const submitUnlessSending = useCallback(evt => !isSending && onSubmit(evt), [isSending, onSubmit]);
  useSubmitOnHotkey(editorRef.current, submitUnlessSending, keyboardHotkeys);

  return (
    <PlateProvider
      editor={editorRef.current}
      id={editorProps.editorId}
      initialValue={initialValue}
      normalizeInitialValue
      onChange={editorProps.onChange}
    >
      <StyledRoot data-aid="taskEmailEditor" {...dropProps}>
        <AttachmentsDropZone isDragActive={isDragActive} />
        <StyledHeader>
          <Recipients />
          <From />
        </StyledHeader>
        <StyledSubject
          data-aid="taskEmailEditor-subject"
          onBlur={saveSubject}
          onChange={onChangeSubject}
          placeholder={t('Subject')}
          value={subject}
        />
        <StyledActionBar data-aid="taskEmailEditor-formattingBar" role="toolbar">
          <ActionBar>
            <BoldItalicUnderlineControls {...editorProps} />
            <FontColorControls {...editorProps} />
            <TextAlignmentMenu {...editorProps} />
            <ListControls {...editorProps} />
            <TableMenu {...editorProps} />
            <>
              <Separator />
              <LinkButton />
            </>
            <Emoji {...editorProps} />
            <Attachment />
            <InsertInlineImageButton {...editorProps} />
          </ActionBar>
        </StyledActionBar>
        <StyledEditorArea data-aid="taskEmailEditor-body">
          <ComposerEditor
            autoFocus={autoFocus}
            minHeight="4.5rem"
            placeholder={t('Type your reply…')}
            {...editorProps}
          />
          <LinkEditor hotKey="mod+k" />
        </StyledEditorArea>
        <CompositionAttachmentsContainer compositionId={compositionId} />
        <EditorErrors />
        <StyledFollowUp>
          <StyledFollowUpLabel>{t('Follow up by')}</StyledFollowUpLabel>
          <DatetimePickerMenu
            boundByWindow
            onChange={onChangeFollowUp}
            placeholderText={t('Follow-up date')}
            value={followUp}
          />
        </StyledFollowUp>
        <Footer>
          <Button
            buttonType={ButtonTypes.TEXT}
            data-aid="taskEmailEditor-cancel"
            disabled={isSending}
            onClick={onCancel}
          >
            {t('Cancel')}
          </Button>
          <Button
            buttonType={ButtonTypes.PRIMARY}
            data-aid="taskEmailEditor-send"
            disabled={isSending}
            onClick={submitUnlessSending}
          >
            {t('Send')}
          </Button>
        </Footer>

        {forgottenAttachmentText ? (
          <ForgottenAttachmentModal
            onCancel={clearForgottenAttachmentText}
            onSubmit={evt => {
              composerProps.onSubmit(evt);
              clearForgottenAttachmentText();
            }}
            text={forgottenAttachmentText}
          />
        ) : null}
        {showForgottenTextModal ? (
          <ForgottenTextModal
            onCancel={clearShowForgottenTextModal}
            onSubmit={evt => {
              composerProps.onSubmit(evt);
              clearShowForgottenTextModal();
            }}
          />
        ) : null}
        {composerProps.isCancelling ? (
          <ComposerCancelModal onCancel={() => setIsCancelling(false)} onConfirm={onDiscard} />
        ) : null}
      </StyledRoot>
    </PlateProvider>
  );
}

TaskThreadReplyComposerBase.propTypes = {
  autoFocus: PropTypes.bool,
  isSending: PropTypes.bool,
  onDiscard: PropTypes.func.isRequired,
};

// Answers and suggested replies are written for the customer, and this email goes to a third party. The
// main composer is mounted alongside, so they would also land here meant for it.
const VENDOR_EDITOR_OPTIONS = { withAnswerPanel: false, withSuggestedReplies: false };

const StyledRoot = styled.div`
  padding: 0 16px;
  position: relative;
`;

const StyledHeader = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-bottom: 8px;
`;

const StyledSubject = styled.input`
  border: 1px solid ${p => p.theme.colors.gray400};
  border-radius: 6px;
  margin-bottom: 8px;
  outline: none;
  padding: 8px 12px;
  width: 100%;

  &:focus {
    border-color: ${p => p.theme.colors.green400};
  }
`;

// The composer's bar, minus its drop shadow: in the panel it sits flush on the editor, not floating
// over a conversation. ActionBar only forwards className, so the test id lives on a wrapper.
const StyledActionBar = styled.div`
  display: flex;
  margin-bottom: 8px;
  min-width: 0;

  > div > span {
    border: none;
    box-shadow: none;
    padding-left: 0;
  }
`;

// TextEditorWrapper and TextEditor both set the editable's min-height with !important at equal
// specificity, so the three-line minimum needs a more specific selector (&&) to win.
const StyledEditorArea = styled(TextEditorWrapper)`
  margin-top: 0;

  && [data-slate-editor='true'] {
    max-height: 200px;
    min-height: 4.5rem !important;
    overflow-y: auto;
  }
`;

const StyledFollowUp = styled.div`
  align-items: center;
  display: flex;
  margin-top: 8px;
`;

const StyledFollowUpLabel = styled.span`
  color: ${p => p.theme.colors.gray600};
  font-size: 12px;
  margin-right: 8px;
`;

// Same height as the customer footer the composer's buttons normally live in, so the two rows line up.
const Footer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  height: 60px;
  justify-content: flex-end;
`;

function mapStateToProps({ getProvider }, { taskId }) {
  const compositionsProvider = getProvider('compositions');
  const composition = findTaskThreadReplyComposition(compositionsProvider, taskId);

  return {
    composition,
    compositionErrors: composition ? compositionsProvider.getErrors(composition.id) : [],
    isExperimentalEditorEnabled: getProvider('agentPreferences').get().experimentalEditorEnabled,
    isLoadingCompositions: compositionsProvider.isLoading(),
    isSending: !!composition && getProvider('inFlightSends').get().has(composition.id),
  };
}

export default connect(mapStateToProps)(TaskThreadReplyComposer);
